// File: src/pages/PurchaseInvoice.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

import { ImDownload } from "react-icons/im";
import { FaFilter, FaChevronDown, FaChevronRight } from "react-icons/fa";
import swal from "sweetalert";
import { ToWords } from "to-words";

import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { PurchaseOrderList, _Delete } from "../import/ApiEndPoint/ApiCalling";
import {
  Completed_Order_Delete,
  Place_Order_Delete,
} from "../import/ApiEndPoint/Api";
import { convertDataCSVtoExcel, exportDataToPDF } from "../import/Downloader";
import { HsnSummaryCalculation } from "../import/HsnSummaryCalculation";
import InvoicGenerator from "../import/PurchaseInvoice";

const injectOnce = () => {
  if (document.getElementById("purchase-invoice-inline-css")) return;

  const style = document.createElement("style");
  style.id = "purchase-invoice-inline-css";

  style.innerHTML = `
    .pi-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:900; padding:6px 10px; border-radius:999px;
    }

    .pi-card{ border:1px solid #e5e7eb; border-radius:14px; background:#f4f7f6; padding:10px 12px; }
    .pi-title{ font-weight:900; text-transform:uppercase; font-size:18px; letter-spacing:.02em; display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
    .pi-actions{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; justify-content:flex-end; }
    .pi-search{ min-width:260px; }

    .pi-btn{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em; text-transform:uppercase;
    }

    .pi-btn-outline{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em;
      background:#fff;
    }

    .pi-filterbar{
      margin-top:10px;
      background:#fff;
      border:1px dashed #cbd5e1;
      border-radius:14px;
      padding:10px 12px;
    }

    .pi-filter-row{
      display:flex;
      gap:10px;
      flex-wrap:wrap;
      align-items:flex-end;
    }

    .pi-fitem{ flex:1 1 180px; min-width:180px; }

    .pi-fitem label{
      font-size:12px;
      font-weight:900;
      color:#0f172a;
      letter-spacing:.02em;
      margin-bottom:4px;
    }

    .pi-fitem .form-control{ border-radius:12px; }

    .pi-chipline{
      margin-top:10px;
      display:flex;
      flex-wrap:wrap;
      gap:8px;
    }

    .pi-chip{
      display:inline-flex;
      align-items:center;
      gap:8px;
      padding:6px 10px;
      border-radius:999px;
      background:#f1f5f9;
      border:1px solid #e5e7eb;
      color:#0f172a;
      font-weight:900;
      font-size:12px;
      letter-spacing:.02em;
    }

    .pi-table-wrap{
      width:100%;
      overflow:auto;
      border:1px solid #e5e7eb;
      border-radius:14px;
      background:#fff;
    }

    table.pi-table{
      width:100%;
      border-collapse:collapse;
      table-layout:auto;
      min-width:1200px;
    }

    .pi-table thead th{
      background:#f4f7f6;
      color:#0f172a;
      font-weight:900;
      text-transform:uppercase;
      font-size:12px;
      letter-spacing:.04em;
      border-bottom:1px solid #e5e7eb;
      padding:8px 10px;
      position:sticky;
      top:0;
      z-index:1;
      text-align:center;
      white-space:nowrap;
    }

    .pi-table tbody td{
      padding:8px 10px;
      border-bottom:1px solid #f1f5f9;
      vertical-align:middle;
      color:#0f172a;
      font-size:14px;
      text-align:center;
    }

    .pi-table tbody tr:hover{ background:#f9fbff; }

    .pi-month-row td{
      background:#eef6ff;
      font-weight:900;
      cursor:pointer;
    }

    .pi-month-row:hover td{ background:#e4efff; }

    .pi-month-name{
      display:flex;
      align-items:center;
      gap:8px;
      justify-content:flex-start;
    }

    .pi-final-row td{
      background:#f8fafc;
      font-weight:900;
    }

    .pi-muted{ color:#64748b; }

    .picker-list{
      border:1px solid #e5e7eb;
      border-radius:10px;
      max-height:280px;
      overflow:auto;
      background:#fff;
      padding:8px;
    }

    .pick-item{
      padding:8px 10px;
      border-radius:8px;
      border:1px solid #e5e7eb;
      margin-bottom:6px;
      background:#fff;
      display:flex;
      align-items:center;
      gap:8px;
      cursor:pointer;
    }

    .nowrap{ white-space:nowrap; }
  `;

  document.head.appendChild(style);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const toNum = (v, d = 0) => {
  const n = Number(String(v ?? "").replace(/[^\d.\-]/g, ""));
  return Number.isFinite(n) ? n : d;
};

const formatSmart = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const includesCompleted = (s = "") =>
  String(s).toLowerCase().includes("completed");

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(+d)) return "";
  return d.toISOString().split("T")[0];
};

const getRowDate = (r) => {
  const d = r?.date || r?.invoiceDate || r?.billDate || r?.createdAt || "";
  const t = d ? new Date(d) : null;
  return t && !isNaN(+t) ? t : null;
};

const getIGSTTotal = (r) => toNum(r?.igstTotal ?? r?.igst, 0);
const getCGSTTotal = (r) => toNum(r?.cgstTotal ?? r?.cgst, 0);
const getSGSTTotal = (r) => toNum(r?.sgstTotal ?? r?.sgst, 0);

const getAdditionalCharges = (r) =>
  toNum(r?.additionalCharges, NaN) === toNum(r?.additionalCharges, NaN)
    ? toNum(r?.additionalCharges, 0)
    : toNum(r?.transportationCost, 0) +
      toNum(r?.labourCost, 0) +
      toNum(r?.localFreight, 0) +
      toNum(r?.miscellaneousCost, 0) +
      toNum(r?.tax, 0);

const getPackages = (r) =>
  toNum(r?.NoOfPackage ?? r?.NoOfPackages ?? r?.noOfPackage, 0);

const normalizeInvoiceRow = (x, idx = 0) => {
  const partyObj = x?.partyId && typeof x.partyId === "object" ? x.partyId : {};

  const invoiceDate =
    x?.date || x?.invoiceDate || x?.billDate || x?.createdAt || "";

  return {
    ...x,

    _rowKey:
      x?._id ||
      `${x?.invoiceId || x?.invoiceNo || "INV"}-${
        invoiceDate || "NO_DATE"
      }-${idx}`,

    date: invoiceDate,

    invoiceId: x?.invoiceId ?? x?.invoiceNo ?? x?.billNo ?? "-",
    status: x?.status ?? "-",

    partyId: partyObj,

    contactNumber:
      partyObj?.contactNumber ??
      x?.contactNumber ??
      x?.partyContactNumber ??
      "-",

    companyName:
      partyObj?.CompanyName ??
      x?.companyName ??
      x?.CompanyName ??
      x?.partyCompanyName ??
      "-",

    ownerName: partyObj?.ownerName ?? x?.ownerName ?? x?.partyOwnerName ?? "-",

    BuiltyNumber:
      x?.BuiltyNumber ?? x?.biltyNumber ?? x?.builtyNo ?? x?.biltyNo ?? "-",

    NoOfPackage: x?.NoOfPackage ?? x?.NoOfPackages ?? x?.noOfPackage ?? 0,

    vehicleNo:
      x?.vehicleNo ?? x?.vehicleNumber ?? x?.truckNo ?? x?.vehicalNo ?? "-",

    igstTotal: getIGSTTotal(x),
    cgstTotal: getCGSTTotal(x),
    sgstTotal: getSGSTTotal(x),

    transportationCost: toNum(x?.transportationCost, 0),
    labourCost: toNum(x?.labourCost, 0),
    localFreight: toNum(x?.localFreight, 0),
    miscellaneousCost: toNum(x?.miscellaneousCost, 0),
    tax: toNum(x?.tax, 0),

    additionalCharges: getAdditionalCharges(x),

    amount: toNum(
      x?.amount ?? x?.subTotal ?? x?.taxableAmount ?? x?.basicAmount,
      0,
    ),

    coolieAndCartage: toNum(x?.coolieAndCartage ?? x?.charges ?? x?.cartage, 0),
    roundOff: toNum(x?.roundOff, 0),
    grandTotal: toNum(x?.grandTotal ?? x?.GrandTotal ?? x?.totalAmount, 0),
  };
};

function getFinancialYearString(dateObj) {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return m >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

function getFinancialYearStartYear(fy) {
  const n = Number(String(fy).split("-")[0]);
  return Number.isFinite(n) ? n : new Date().getFullYear();
}

function getFYMonthMeta(dateObj) {
  const d = new Date(dateObj);
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  const fy = getFinancialYearString(d);

  const labelMap = {
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
    1: "January",
    2: "February",
    3: "March",
  };

  const sortIndexMap = {
    4: 1,
    5: 2,
    6: 3,
    7: 4,
    8: 5,
    9: 6,
    10: 7,
    11: 8,
    12: 9,
    1: 10,
    2: 11,
    3: 12,
  };

  const fyStart = getFinancialYearStartYear(fy);
  const displayYear = month >= 4 ? fyStart : fyStart + 1;

  return {
    fy,
    month,
    label: `${labelMap[month]} ${displayYear}`,
    sortIndex: sortIndexMap[month],
    displayYear,
    realYear: year,
    fyStartYear: fyStart,
  };
}

const makeTotals = (rows = []) => ({
  igstTotal: rows.reduce((acc, r) => acc + toNum(r?.igstTotal, 0), 0),
  cgstTotal: rows.reduce((acc, r) => acc + toNum(r?.cgstTotal, 0), 0),
  sgstTotal: rows.reduce((acc, r) => acc + toNum(r?.sgstTotal, 0), 0),
  additionalCharges: rows.reduce(
    (acc, r) => acc + toNum(r?.additionalCharges, 0),
    0,
  ),
  amount: rows.reduce((acc, r) => acc + toNum(r?.amount, 0), 0),
  charges: rows.reduce((acc, r) => acc + toNum(r?.coolieAndCartage, 0), 0),
  roundOff: rows.reduce((acc, r) => acc + toNum(r?.roundOff, 0), 0),
  grandTotal: rows.reduce((acc, r) => acc + toNum(r?.grandTotal, 0), 0),
  packages: rows.reduce((acc, r) => acc + getPackages(r), 0),
});

function groupRowsByMonth(rows = []) {
  const bucket = {};

  rows.forEach((r) => {
    const dt = getRowDate(r);
    if (!dt) return;

    const meta = getFYMonthMeta(dt);
    const key = `${meta.fy}-${meta.month}`;

    if (!bucket[key]) {
      bucket[key] = {
        key,
        label: meta.label,
        month: meta.month,
        fy: meta.fy,
        sortIndex: meta.sortIndex,
        fyStartYear: meta.fyStartYear,
        rows: [],
      };
    }

    bucket[key].rows.push(r);
  });

  return Object.values(bucket)
    .map((g) => ({
      ...g,
      rows: g.rows.sort(
        (a, b) =>
          (getRowDate(b)?.getTime?.() || 0) - (getRowDate(a)?.getTime?.() || 0),
      ),
      totals: makeTotals(g.rows),
    }))
    .sort((a, b) => {
      if (a.fyStartYear !== b.fyStartYear) return b.fyStartYear - a.fyStartYear;
      return a.sortIndex - b.sortIndex;
    });
}

const buildCSV = (data, cols) => {
  const head = cols.map((c) => c.header).join(",");

  const lines = (data || []).map((r, ridx) =>
    cols
      .map((c) => {
        const raw = typeof c.get === "function" ? c.get(r, ridx) : r?.[c.key];
        const value = toStr(raw).replace(/"/g, '""');
        return `"${value}"`;
      })
      .join(","),
  );

  return [head, ...lines].join("\r\n");
};

const toWords = new ToWords({
  localeCode: "en-IN",
  converterOptions: {
    currency: true,
    ignoreDecimal: false,
    ignoreZeroCurrency: false,
    doNotAddOnly: false,
    currencyOptions: {
      name: "Rupee",
      plural: "Rupees",
      symbol: "₹",
      fractionalUnit: {
        name: "Paisa",
        plural: "Paise",
        symbol: "",
      },
    },
  },
});

export default function PurchaseInvoice() {
  const hideTimer = useRef(null);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");

  const [masterShow, setMasterShow] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [fStatus, setFStatus] = useState("Completed");
  const [fCompany, setFCompany] = useState("");
  const [fInv, setFInv] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fMinGrand, setFMinGrand] = useState("");
  const [fMaxGrand, setFMaxGrand] = useState("");

  const [fy, setFy] = useState("ALL");
  const [monthExpanded, setMonthExpanded] = useState({});

  const [modal, setModal] = useState(false);
  const [arrIndex, setArrIndex] = useState(-1);
  const [checkedToAdd, setCheckedToAdd] = useState([]);

  const [selectedKeys, setSelectedKeys] = useState([]);

  const [billOpen, setBillOpen] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [wordsNumber, setWordsNumber] = useState("");
  const [appliedCharges] = useState({});
  const [allBillMerged] = useState([]);
  const [otherCharges] = useState("");
  const [discount] = useState("");
  const [billNumber, setBillNumber] = useState("");

  useEffect(() => injectOnce(), []);

  const handleDelete = (id, status) => {
    const URL = includesCompleted(status)
      ? Completed_Order_Delete
      : Place_Order_Delete;

    swal("Warning", "Sure You Want to Delete it", {
      buttons: {
        cancel: "Cancel",
        catch: {
          text: "Delete",
          value: "delete",
        },
      },
    }).then(async (value) => {
      if (value === "delete") {
        try {
          await _Delete(URL, id);

          setRows((prev) => prev.filter((x) => x?._id !== id));
          setAllRows((prev) => prev.filter((x) => x?._id !== id));
        } catch {}
      }
    });
  };

  const handlePreviewInvoice = (data) => {
    if (!includesCompleted(data?.status)) {
      swal("Invoice not completed");
      return;
    }

    const value = {
      ...data,
      purchaseStatus: true,
    };

    const gt = Number((value?.grandTotal || 0).toFixed(2));
    const decimals = Math.round((gt - Math.floor(gt)) * 100);

    if (decimals > 0) {
      if (decimals > 49) {
        const roundoff = (100 - decimals) / 100;
        value.grandTotal = parseFloat((gt + roundoff).toFixed(2));
        value.roundOff = +roundoff;
      } else {
        const roundoff = -(decimals / 100);
        value.grandTotal = parseFloat((gt + roundoff).toFixed(2));
        value.roundOff = +roundoff;
      }
    } else {
      value.grandTotal = gt;
    }

    value.gstDetails = HsnSummaryCalculation(value);

    const words = toWords.convert(Number(value.grandTotal), {
      currency: true,
    });

    setWordsNumber(words);
    setPrintData(value);
    setBillNumber(String(value?.invoiceId || ""));
    setBillOpen(true);
  };

  const baseColumns = useMemo(
    () => [
      {
        key: "_uid",
        header: "S.NO",
        get: (_r, i) => i + 1,
      },
      {
        key: "status",
        header: "STATUS",
        get: (r) => r?.status || "-",
      },
      {
        key: "invoiceId",
        header: "INVOICE NO.",
        get: (r) => r?.invoiceId || "-",
        render: (r) => (
          <span
            onClick={() => handleDelete(r?._id, r?.status)}
            style={{
              color: "#ff000d",
              cursor: "pointer",
              fontWeight: 800,
            }}
            title="Delete this invoice"
          >
            {r?.invoiceId || "-"}
          </span>
        ),
      },
      {
        key: "date",
        header: "DATE",
        get: (r) => formatDate(r?.date),
      },
      {
        key: "partyId.contactNumber",
        header: "CONTACT",
        get: (r) => r?.contactNumber || "-",
      },
      {
        key: "partyId.CompanyName",
        header: "COMPANY",
        get: (r) => r?.companyName || "-",
        render: (r) => (
          <Link
            to={`/app/purchase-invoice/edit/${r?._id}`}
            style={{
              color: "#78ff73",
              cursor: "pointer",
              fontWeight: 800,
              textDecoration: "none",
            }}
            title="Edit invoice"
          >
            {r?.companyName || "-"}
          </Link>
        ),
      },
      {
        key: "partyId.ownerName",
        header: "OWNER",
        get: (r) => r?.ownerName || "-",
      },
      {
        key: "BuiltyNumber",
        header: "BUILTY NO.",
        get: (r) => r?.BuiltyNumber || "-",
      },
      {
        key: "NoOfPackage",
        header: "PACKAGES",
        get: (r) => r?.NoOfPackage || "-",
      },
      {
        key: "vehicleNo",
        header: "VEHICLE NO",
        get: (r) => r?.vehicleNo || "-",
      },
      {
        key: "igstTotal",
        header: "IGST",
        get: (r) => toNum(r?.igstTotal, 0),
      },
      {
        key: "cgstTotal",
        header: "CGST",
        get: (r) => toNum(r?.cgstTotal, 0),
      },
      {
        key: "sgstTotal",
        header: "SGST",
        get: (r) => toNum(r?.sgstTotal, 0),
      },
      {
        key: "additionalCharges",
        header: "ADDITIONAL CHARGES",
        get: (r) => toNum(r?.additionalCharges, 0),
      },
      {
        key: "amount",
        header: "AMOUNT",
        get: (r) => toNum(r?.amount, 0),
      },
      {
        key: "coolieAndCartage",
        header: "CHARGES",
        get: (r) => toNum(r?.coolieAndCartage, 0),
      },
      {
        key: "roundOff",
        header: "ROUND OFF",
        get: (r) => toNum(r?.roundOff, 0),
      },
      {
        key: "grandTotal",
        header: "GRAND TOTAL",
        get: (r) => toNum(r?.grandTotal, 0),
        render: (r) => (
          <span
            onClick={() => handlePreviewInvoice(r)}
            style={{
              color: "#649bfa",
              cursor: "pointer",
              fontWeight: 800,
            }}
            title="Preview invoice"
          >
            {toNum(r?.grandTotal, 0)}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions],
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  useEffect(() => {
    const raw = localStorage.getItem("PurchaseInvoiceList");

    let next = [];

    try {
      const parsed = raw ? JSON.parse(raw) : null;

      if (Array.isArray(parsed) && parsed.length) {
        const maybeKeys = parsed.map((c) => c.key || c?.field || c);
        next = maybeKeys.filter((k) => colMap.has(k));
      }
    } catch {}

    if (!next.length) {
      next = baseColumns.map((c) => c.key);
    }

    setSelectedKeys(next);
  }, [baseColumns, colMap]);

  const reload = async (uid, db) => {
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await PurchaseOrderList(uid, db);
      const list = Array.isArray(res?.orderHistory) ? res.orderHistory : [];

      const normalized = list.map((x, idx) => normalizeInvoiceRow(x, idx));

      // ONLY COMPLETED PURCHASE ORDERS / INVOICES WILL SHOW IN THIS PAGE
      const completedOnly = normalized.filter((x) =>
        includesCompleted(x?.status),
      );

      const final = completedOnly.slice().reverse();

      setRows(final);
      setAllRows(final);
    } catch (e) {
      console.error("[PurchaseInvoice] fetch error:", e);

      setErrorMsg(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to load purchase invoices.",
      );

      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const inside = CheckPermission("Purchase Invoice");

      if (inside && typeof inside === "object") {
        setPermissions((p) => ({
          ...p,
          ...inside,
        }));
      }

      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const financialYearSelected = localStorage.getItem(
        "financialYearSelected",
      );

      const selectedDatabase = `${user?.database}-${financialYearSelected}`;

      if (user?.rolename?.roleName === "MASTER") {
        setMasterShow(true);
      }

      await reload(user?._id, selectedDatabase);
    })();
  }, []);

  const fyOptions = useMemo(() => {
    const fySet = [
      ...new Set(
        allRows
          .map((r) => getRowDate(r))
          .filter(Boolean)
          .map((d) => getFinancialYearString(d)),
      ),
    ].sort((a, b) => Number(b.split("-")[0]) - Number(a.split("-")[0]));

    return fySet;
  }, [allRows]);

  const onMasterSubmit = (e) => {
    e.preventDefault();

    const pack = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || '""',
    );

    if (!pack) return;

    const [id, db] = String(pack).split(" ");
    reload(id, db);
  };

  const onMasterChange = (v) =>
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(v));

  const onDlEnter = () => {
    clearTimeout(hideTimer.current);
  };

  const onDlLeave = () => {
    hideTimer.current = setTimeout(() => {}, 180);
  };

  const openPicker = () => {
    setCheckedToAdd([]);
    setArrIndex(-1);
    setModal(true);
  };

  const closePicker = () => setModal(false);

  const handleCheckToAdd = (checked, key) => {
    setCheckedToAdd((prev) => {
      const next = new Set(prev);

      if (checked) next.add(key);
      else next.delete(key);

      return Array.from(next);
    });
  };

  const addSelectedColumns = () => {
    setSelectedKeys((prev) => Array.from(new Set([...prev, ...checkedToAdd])));
  };

  const removeAtIndex = () => {
    if (arrIndex < 0) return;

    setSelectedKeys((prev) => {
      const next = prev.slice();
      next.splice(arrIndex, 1);
      return next;
    });

    setArrIndex(-1);
  };

  const shiftUp = () => {
    if (arrIndex <= 0) return;

    setSelectedKeys((prev) => {
      const next = prev.slice();
      const el = next.splice(arrIndex, 1)[0];
      next.splice(arrIndex - 1, 0, el);
      return next;
    });

    setArrIndex((i) => i - 1);
  };

  const shiftDown = () => {
    setSelectedKeys((prev) => {
      if (arrIndex < 0 || arrIndex >= prev.length - 1) return prev;

      const next = prev.slice();
      const el = next.splice(arrIndex, 1)[0];
      next.splice(arrIndex + 1, 0, el);

      setArrIndex((i) => i + 1);

      return next;
    });
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("PurchaseInvoiceList", JSON.stringify(selectedKeys));
    closePicker();
  };

  const filteredByFilters = useMemo(() => {
    const companyTerm = String(fCompany || "")
      .trim()
      .toLowerCase();
    const invTerm = String(fInv || "")
      .trim()
      .toLowerCase();

    const from = fFrom ? new Date(`${fFrom}T00:00:00`) : null;
    const to = fTo ? new Date(`${fTo}T23:59:59`) : null;

    const minG = fMinGrand !== "" ? Number(fMinGrand) : null;
    const maxG = fMaxGrand !== "" ? Number(fMaxGrand) : null;

    const base = rows || [];

    return base.filter((r) => {
      // Extra safety: always keep only completed
      if (!includesCompleted(r?.status)) return false;

      if (fy && fy !== "ALL") {
        const dt = getRowDate(r);
        if (!dt || getFinancialYearString(dt) !== fy) return false;
      }

      if (companyTerm) {
        const nm = String(r?.companyName || "").toLowerCase();
        if (!nm.includes(companyTerm)) return false;
      }

      if (invTerm) {
        const inv = String(r?.invoiceId || "").toLowerCase();
        if (!inv.includes(invTerm)) return false;
      }

      const dt = getRowDate(r);

      if (from && dt && dt < from) return false;
      if (to && dt && dt > to) return false;

      const g = toNum(r?.grandTotal, 0);

      if (minG !== null && Number.isFinite(minG) && g < minG) return false;
      if (maxG !== null && Number.isFinite(maxG) && g > maxG) return false;

      return true;
    });
  }, [rows, fy, fCompany, fInv, fFrom, fTo, fMinGrand, fMaxGrand]);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap],
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return filteredByFilters;

    return (filteredByFilters || []).filter((r, i) =>
      visibleCols.some((c) =>
        toStr(typeof c.get === "function" ? c.get(r, i) : r?.[c.key])
          .toLowerCase()
          .includes(term),
      ),
    );
  }, [filteredByFilters, search, visibleCols]);

  const groupedRows = useMemo(
    () => groupRowsByMonth(filteredRows),
    [filteredRows],
  );

  useEffect(() => {
    const initial = {};

    groupedRows.forEach((g) => {
      initial[g.key] = false;
    });

    setMonthExpanded(initial);
  }, [groupedRows]);

  const toggleMonth = (key) => {
    setMonthExpanded((prev) => {
      const isCurrentlyOpen = !!prev[key];
      const next = {};

      Object.keys(prev).forEach((k) => {
        next[k] = false;
      });

      next[key] = !isCurrentlyOpen;

      return next;
    });
  };

  const chips = useMemo(() => {
    const out = [];

    out.push({
      k: "FY",
      v: fy === "ALL" ? "All Financial Years" : fy,
    });

    out.push({
      k: "Status",
      v: "Completed Only",
    });

    if (fCompany.trim()) {
      out.push({
        k: "Company",
        v: fCompany.trim(),
      });
    }

    if (fInv.trim()) {
      out.push({
        k: "Invoice",
        v: fInv.trim(),
      });
    }

    if (fFrom) {
      out.push({
        k: "From",
        v: fFrom,
      });
    }

    if (fTo) {
      out.push({
        k: "To",
        v: fTo,
      });
    }

    if (fMinGrand !== "") {
      out.push({
        k: "Min Total",
        v: fMinGrand,
      });
    }

    if (fMaxGrand !== "") {
      out.push({
        k: "Max Total",
        v: fMaxGrand,
      });
    }

    return out;
  }, [fy, fCompany, fInv, fFrom, fTo, fMinGrand, fMaxGrand]);

  const clearFilters = () => {
    setFStatus("Completed");
    setFCompany("");
    setFInv("");
    setFFrom("");
    setFTo("");
    setFMinGrand("");
    setFMaxGrand("");
    setFy("ALL");
  };

  const exportCols = useMemo(() => visibleCols, [visibleCols]);

  const exportPDF = async () => {
    const csv = buildCSV(filteredRows, exportCols);
    await exportDataToPDF(csv, "PurchaseInvoiceList");
  };

  const exportCSV = () => {
    const csv = buildCSV(filteredRows, exportCols);

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "PurchaseInvoiceList.csv";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  };

  const exportXLS = async () => {
    const csv = buildCSV(filteredRows, exportCols);
    await convertDataCSVtoExcel(csv, "PurchaseInvoiceList");
  };

  const grandTotals = useMemo(() => makeTotals(filteredRows), [filteredRows]);

  const renderGroupCell = (c, group, colIndex) => {
    if (colIndex === 0) {
      return (
        <div className="pi-month-name">
          {monthExpanded[group.key] ? <FaChevronDown /> : <FaChevronRight />}
          <span>{group.label}</span>
        </div>
      );
    }

    switch (c.key) {
      case "status":
        return "Month Total";
      case "igstTotal":
        return formatSmart(group.totals.igstTotal);
      case "cgstTotal":
        return formatSmart(group.totals.cgstTotal);
      case "sgstTotal":
        return formatSmart(group.totals.sgstTotal);
      case "additionalCharges":
        return formatSmart(group.totals.additionalCharges);
      case "amount":
        return formatSmart(group.totals.amount);
      case "coolieAndCartage":
        return formatSmart(group.totals.charges);
      case "roundOff":
        return formatSmart(group.totals.roundOff);
      case "grandTotal":
        return formatSmart(group.totals.grandTotal);
      case "NoOfPackage":
        return formatSmart(group.totals.packages);
      default:
        return "—";
    }
  };

  const renderFinalCell = (c, colIndex) => {
    if (colIndex === 0) return "Final Total";

    switch (c.key) {
      case "igstTotal":
        return formatSmart(grandTotals.igstTotal);
      case "cgstTotal":
        return formatSmart(grandTotals.cgstTotal);
      case "sgstTotal":
        return formatSmart(grandTotals.sgstTotal);
      case "additionalCharges":
        return formatSmart(grandTotals.additionalCharges);
      case "amount":
        return formatSmart(grandTotals.amount);
      case "coolieAndCartage":
        return formatSmart(grandTotals.charges);
      case "roundOff":
        return formatSmart(grandTotals.roundOff);
      case "grandTotal":
        return formatSmart(grandTotals.grandTotal);
      case "NoOfPackage":
        return formatSmart(grandTotals.packages);
      default:
        return "—";
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows.length;

  return (
    <>
      <Row className="pi-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="pi-card">
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div className="pi-title">
                Purchase Invoice{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
                <span
                  style={{
                    fontWeight: 800,
                    color: "#64748b",
                    fontSize: 12,
                  }}
                >
                  (Completed Only)
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              {masterShow ? (
                <div style={{ minWidth: 320, display: "flex", gap: 8 }}>
                  <SuperAdminUI
                    onDropdownChange={onMasterChange}
                    onSubmit={onMasterSubmit}
                  />
                </div>
              ) : null}

              <div className="pi-actions">
                {permissions?.Create && (
                  <Link to="/app/purchase-invoice/create">
                    <Button
                      className="pi-btn"
                      color="primary"
                      title="Create Purchase Invoice"
                    >
                      Create Invoice
                    </Button>
                  </Link>
                )}

                <div className="pi-search">
                  <Input
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ background: "#fff" }}
                  />
                </div>

                <Button
                  className="pi-btn-outline"
                  outline
                  color="primary"
                  onClick={() => setShowFilters((s) => !s)}
                  title="Show/Hide Filters"
                >
                  <FaFilter size={14} /> Filters
                </Button>

                {permissions?.View && (
                  <Button
                    className="pi-btn-outline"
                    outline
                    color="primary"
                    onClick={openPicker}
                    title="Choose Columns"
                  >
                    <FaFilter size={14} /> Columns
                  </Button>
                )}

                {permissions?.Download && (
                  <>
                    <Button
                      className="pi-btn"
                      color="primary"
                      onClick={exportXLS}
                      title="Download Excel"
                    >
                      <ImDownload /> Excel
                    </Button>

                    <Button
                      className="pi-btn-outline"
                      outline
                      color="primary"
                      onClick={exportPDF}
                      title="Download PDF"
                    >
                      <ImDownload /> PDF
                    </Button>

                    <Button
                      className="pi-btn-outline"
                      outline
                      color="primary"
                      onClick={exportCSV}
                      title="Download CSV"
                    >
                      <ImDownload /> CSV
                    </Button>
                  </>
                )}
              </div>
            </div>

            {errorMsg ? (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #fecaca",
                  color: "#b91c1c",
                  fontWeight: 800,
                }}
              >
                {errorMsg}
              </div>
            ) : null}

            {showFilters && (
              <div className="pi-filterbar">
                <div className="pi-filter-row">
                  <div className="pi-fitem">
                    <label>Financial Year</label>
                    <Input
                      type="select"
                      value={fy}
                      onChange={(e) => setFy(e.target.value)}
                    >
                      <option value="ALL">All Financial Years</option>
                      {fyOptions.map((x) => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ))}
                    </Input>
                  </div>

                  <div className="pi-fitem">
                    <label>Status</label>
                    <Input type="select" value="Completed" disabled>
                      <option value="Completed">Completed Only</option>
                    </Input>
                  </div>

                  <div className="pi-fitem">
                    <label>Company contains</label>
                    <Input
                      placeholder="Type company name..."
                      value={fCompany}
                      onChange={(e) => setFCompany(e.target.value)}
                    />
                  </div>

                  <div className="pi-fitem">
                    <label>Invoice No contains</label>
                    <Input
                      placeholder="Type invoice no..."
                      value={fInv}
                      onChange={(e) => setFInv(e.target.value)}
                    />
                  </div>

                  <div className="pi-fitem">
                    <label>Date From</label>
                    <Input
                      type="date"
                      value={fFrom}
                      onChange={(e) => setFFrom(e.target.value)}
                    />
                  </div>

                  <div className="pi-fitem">
                    <label>Date To</label>
                    <Input
                      type="date"
                      value={fTo}
                      onChange={(e) => setFTo(e.target.value)}
                    />
                  </div>

                  <div className="pi-fitem">
                    <label>Min Grand Total</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={fMinGrand}
                      onChange={(e) => setFMinGrand(e.target.value)}
                    />
                  </div>

                  <div className="pi-fitem">
                    <label>Max Grand Total</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={fMaxGrand}
                      onChange={(e) => setFMaxGrand(e.target.value)}
                    />
                  </div>

                  <div className="pi-fitem" style={{ minWidth: 220 }}>
                    <label>Actions</label>

                    <div style={{ display: "flex", gap: 8 }}>
                      <Button
                        className="pi-btn-outline"
                        outline
                        color="secondary"
                        onClick={clearFilters}
                      >
                        Clear
                      </Button>

                      <Button
                        className="pi-btn-outline"
                        outline
                        color="primary"
                        onClick={() => {
                          const u = JSON.parse(
                            localStorage.getItem("userData") || "{}",
                          );

                          const financialYearSelected = localStorage.getItem(
                            "financialYearSelected",
                          );

                          const selectedDatabase = `${u?.database}-${financialYearSelected}`;

                          reload(u?._id, selectedDatabase);
                        }}
                        title="Refresh"
                      >
                        Refresh
                      </Button>
                    </div>
                  </div>
                </div>

                {!!chips.length && (
                  <div className="pi-chipline">
                    {chips.map((c, i) => (
                      <span className="pi-chip" key={`${c.k}-${i}`}>
                        {c.k}: {c.v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  color: "#6b7280",
                }}
              >
                No completed purchase invoices found.
              </div>
            ) : (
              <div className="pi-table-wrap">
                <table className="pi-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {groupedRows.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr
                          className="pi-month-row"
                          onClick={() => toggleMonth(group.key)}
                        >
                          {visibleCols.map((c, colIndex) => (
                            <td key={`${group.key}-${c.key}`}>
                              {renderGroupCell(c, group, colIndex)}
                            </td>
                          ))}
                        </tr>

                        {monthExpanded[group.key] &&
                          group.rows.map((r, ri) => (
                            <tr key={r?._rowKey || `${group.key}-${ri}`}>
                              {visibleCols.map((c, ci) => {
                                if (c.key === "_uid") {
                                  return (
                                    <td key={`${r?._rowKey || ri}-${ci}`}>
                                      <span className="pi-muted">
                                        #{ri + 1}
                                      </span>
                                    </td>
                                  );
                                }

                                if (typeof c.render === "function") {
                                  return (
                                    <td key={`${r?._rowKey || ri}-${ci}`}>
                                      {c.render(r, ri)}
                                    </td>
                                  );
                                }

                                const value =
                                  typeof c.get === "function"
                                    ? c.get(r, ri)
                                    : r?.[c.key];

                                return (
                                  <td key={`${r?._rowKey || ri}-${ci}`}>
                                    {toStr(value)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}

                    <tr className="pi-final-row">
                      {visibleCols.map((c, colIndex) => (
                        <td key={`final-${c.key}`}>
                          {renderFinalCell(c, colIndex)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Modal isOpen={modal} toggle={closePicker} style={{ maxWidth: 1050 }}>
        <ModalHeader toggle={closePicker}>Change Fields</ModalHeader>

        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>

              <div className="picker-list">
                {baseColumns.map((col) => (
                  <label
                    key={col.key}
                    className="pick-item"
                    style={{ cursor: "default" }}
                  >
                    <input
                      type="checkbox"
                      checked={checkedToAdd.includes(col.key)}
                      onChange={(e) =>
                        handleCheckToAdd(e.target.checked, col.key)
                      }
                    />

                    <span>{col.header}</span>
                  </label>
                ))}
              </div>

              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col
              lg="2"
              md="2"
              sm="12"
              xl="2"
              xs="12"
              style={{ display: "grid", placeItems: "center" }}
            >
              <div style={{ display: "grid", gap: 10 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add →
                </Button>

                <Button size="sm" color="danger" onClick={removeAtIndex}>
                  ← Remove
                </Button>
              </div>
            </Col>

            <Col lg="6" md="6" sm="12" xl="6" xs="12">
              <Row>
                <Col lg="8" md="8" sm="12" xs="12">
                  <h5>Visible Columns</h5>

                  <div className="picker-list">
                    {selectedKeys.map((key, i) => {
                      const col = colMap.get(key);

                      if (!col) return null;

                      return (
                        <div
                          key={key}
                          className="pick-item"
                          onClick={() => setArrIndex(i)}
                          style={{
                            background: i === arrIndex ? "#1877f2" : "#fff",
                            color: i === arrIndex ? "#fff" : "#000",
                          }}
                        >
                          <span style={{ fontWeight: 900 }}>{col.header}</span>
                        </div>
                      );
                    })}
                  </div>
                </Col>

                <Col
                  lg="4"
                  md="4"
                  sm="12"
                  xs="12"
                  style={{ display: "grid", placeItems: "center" }}
                >
                  <div style={{ display: "grid", gap: 10 }}>
                    <Button size="sm" onClick={shiftUp}>
                      ↑ Up
                    </Button>

                    <Button size="sm" onClick={shiftDown}>
                      ↓ Down
                    </Button>

                    <Button size="sm" color="danger" onClick={removeAtIndex}>
                      Remove
                    </Button>
                  </div>
                </Col>
              </Row>

              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={billOpen}
        toggle={() => setBillOpen(false)}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={() => setBillOpen(false)}>
          Download Bill
        </ModalHeader>

        <ModalBody>
          {printData ? (
            <div style={{ width: "100%" }}>
              <InvoicGenerator
                CompanyDetails={{}}
                BillNumber={billNumber}
                PrintData={printData}
                Applied_Charges={appliedCharges}
                AllbillMerged={allBillMerged}
                wordsNumber={wordsNumber}
                otherCharges={otherCharges}
                discount={discount}
              />
            </div>
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
}
